export interface FirmwareBundle {
  softwareVersion: string;
  modemFirmware: string;
  targetBand: string;
  modemModel: string;
  modemVersion: string;
}

export const deviceFirmwareVersion = (version?: string): string =>
  version?.startsWith("MegaFi_2-") ? `${version.slice("MegaFi_2-".length)}_MEGAFI2` : version ?? "";

export const requiresFirmwareBundle = (version: string): boolean => {
  const match = deviceFirmwareVersion(version).match(/^v?(\d+)\.(\d+)\.(\d+)(?:\.\d+)?_MEGAFI2$/);
  if (!match) return false;
  const [major, minor, patch] = match.slice(1).map(Number);
  return major > 3 || (major === 3 && (minor > 9 || (minor === 9 && patch >= 1)));
};

export const canPairFirmwareBundle = (version: string): boolean =>
  requiresFirmwareBundle(version) || /^v?3\.9\.0\.\d+_MEGAFI2$/.test(deviceFirmwareVersion(version));

export const requiresDeviceFirstFirmware = (version?: string): boolean => {
  const match = deviceFirmwareVersion(version).match(/^v?(\d+)\.(\d+)\.(\d+)(?:\.\d+)?_MEGAFI2$/);
  if (!match) return false;
  const [major, minor, patch] = match.slice(1).map(Number);
  return major < 3 || (major === 3 && (minor < 8 || (minor === 8 && patch <= 1)));
};

export const modemFirmwareDisplay = (value?: string | null): string => {
  const version = value?.trim();
  return !version || ["unknown", "nil", "not available"].includes(version.toLowerCase()) ? "Unknown" : version;
};

export const bundleForVersion = (version: string | undefined, bundles: FirmwareBundle[]): FirmwareBundle | undefined =>
  bundles.find((bundle) => bundle.softwareVersion === deviceFirmwareVersion(version));

export const modemMatchesBundle = (
  device: { modemModel?: string; modemFirmwareVersion?: string },
  bundle: FirmwareBundle,
): boolean => {
  if (device.modemModel?.trim().toUpperCase() !== bundle.modemModel.toUpperCase()) return false;
  const current = (device.modemFirmwareVersion ?? "").toUpperCase().split(/\s+/);
  const target = bundle.modemVersion.toUpperCase().split(/\s+/).filter(Boolean);
  return target.length > 0 && target.every((part) => current.includes(part));
};

export const bundleNeedsModemUpdate = (
  device: { modemModel?: string; modemFirmwareVersion?: string },
  version: string,
  bundles: FirmwareBundle[],
): boolean => {
  const bundle = bundleForVersion(version, bundles);
  return Boolean(bundle && !modemMatchesBundle(device, bundle));
};

export const firmwareVersionOptions = (versions: string[], bundles: FirmwareBundle[], currentVersion: string) =>
  versions
    .filter(
      (version) => version === currentVersion || !requiresFirmwareBundle(version) || bundleForVersion(version, bundles),
    )
    .map((version) => {
      const bundle = bundleForVersion(version, bundles);
      const modemVersion = bundle?.modemVersion.match(/C0R\.\d+/i)?.[0] ?? bundle?.modemVersion;
      return { value: version, label: bundle ? `${version} + ${bundle.targetBand} modem (${modemVersion})` : version };
    });
